import type { DataSourceAbstract, FieldInputADTValue, RecordLikeProtocol } from '@lighthouse/core'
import React, { createContext, useContext } from 'react'

import type { FieldInputError, FieldInputErrors } from '../components'

export type SubFormErrors = { recordId: string; blockId: string; error: FieldInputError; columnId: string }[]
export type FormModuleType = {
    /** nearest parent form id (scoped), if any */
    formId?: string
    disabled?: boolean
    pointer: string
    dataSource?: DataSourceAbstract
    children?: React.ReactNode
    errors?: FieldInputErrors
    serviceErrors?: FieldInputErrors
    subFormErrors?: SubFormErrors
    subFormServiceErrors?: SubFormErrors
    record?: RecordLikeProtocol
    type: 'form' | 'field'
    onChange?: (blockId: string, value: FieldInputADTValue) => void
    onBlur?: (blockId: string, value: FieldInputADTValue) => void
    onCodeChange?: (blockId: string, code: string) => void

    /**
     * Child blocks report their own validation results to the form container.
     * Form should not scan child blocks for validation configs.
     */
    reportFieldError?: (blockId: string, error: FieldInputError | undefined) => void
    resetFieldServiceError?: (blockId: string) => void
    updateSubFormErrors?: (updater: (draft: SubFormErrors) => void) => void
    resetSubFormServiceError?: (recordId: string, blockId: string, columnId: string) => void
    /**
     * Set to true when form submit has been attempted. Used by fields to decide
     * whether to show validation errors (show when touched || submitAttempted).
     */
    submitAttempted?: boolean

    /**
     * Incremented when form has been submitted successfully (no repeat errors).
     * Fields use this to reset their value to default.
     */
    formResetKey?: number

    /** used by realtime-update form mode */
    onUpdateCell?: (...args: any[]) => void
}

const FormModuleContext = createContext<FormModuleType>({
    pointer: '',
    type: 'field'
})

export const FormModuleProvider = ({ children, value }: { value: FormModuleType; children?: React.ReactNode }) => {
    return <FormModuleContext.Provider value={value}> {children}</FormModuleContext.Provider>
}

export const useFormModuleContext = () => {
    return useContext(FormModuleContext)
}
